import {computeDeviceStatus} from '@nursio-link/common';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetAllPairedDevicesForExportQuery} from '@/services/graphqlHooks';
import {
  useGetAllPairedDevicesForExportQuery,
  useGetUsersForExportQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import pairedDeviceStatuses from '@/translation/pairedDeviceStatuses';
import pairedDeviceTypes from '@/translation/pairedDeviceTypes';

import type {ExportDefinition} from '../../types/exportTypes';
import {
  formatExportDateTime,
  formatPersonName,
  noOptions,
} from '../exportOptions';
import {fetchAllPages} from '../fetchAllPages';

type PairedDeviceExportRecord =
  GetAllPairedDevicesForExportQuery['getAllPairedDevices']['items'][number] & {
    userName: string;
  };

const fetchDevicesPage = async (nextToken?: string) => {
  const variables = {nextToken};
  const {getAllPairedDevices} = await queryClient.fetchQuery({
    queryKey: useGetAllPairedDevicesForExportQuery.getKey(variables),
    queryFn: useGetAllPairedDevicesForExportQuery.fetcher(
      graphqlClient,
      variables
    ),
  });

  return getAllPairedDevices;
};

export const pairedDevicesExport: ExportDefinition<
  Record<string, never>,
  PairedDeviceExportRecord
> = {
  id: 'pairedDevices',
  label: i18n.t('Paired devices'),
  permission: 'EXPORT_PAIRED_DEVICES',
  ...noOptions,
  load: async () => {
    const [devices, {getAllUsers}] = await Promise.all([
      fetchAllPages(fetchDevicesPage),
      queryClient.fetchQuery({
        queryKey: useGetUsersForExportQuery.getKey(),
        queryFn: useGetUsersForExportQuery.fetcher(graphqlClient),
      }),
    ]);
    const users = new Map(getAllUsers.map(user => [user.userName, user.name]));

    return devices.map(device => ({
      ...device,
      userName:
        formatPersonName(users.get(device.userRequested)) ||
        device.userRequested,
    }));
  },
  columns: [
    {header: i18n.t('Device name'), value: device => device.deviceName},
    {
      header: i18n.t('Type'),
      filterable: true,
      value: device => pairedDeviceTypes[device.deviceType],
    },
    {
      header: i18n.t('User'),
      filterable: true,
      value: device => device.userName,
    },
    {
      header: i18n.t('Status'),
      filterable: true,
      value: device => pairedDeviceStatuses[computeDeviceStatus(device)],
    },
    {
      header: i18n.t('Approved IPs'),
      value: device => device.approvedIps.join(', '),
    },
    {
      header: i18n.t('Pending IPs'),
      value: device => device.pendingIps.join(', '),
    },
    {
      header: i18n.t('Created at'),
      value: device => formatExportDateTime(device.createdAt),
    },
  ],
  getFileName: () => 'paired-devices',
};
